import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileText, Download, Search, FileSpreadsheet, FileCode2, FileCode, Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ErrorState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem } from "@/components/brand/Hero";
import { Toolbar } from "@/components/brand/Toolbar";
import { Card, Mantra, MantraAccent } from "@/components/brand/Card";
import { useProject, useArtifacts } from "@/hooks/useApi";
import { downloadArtifact, type DownloadFormat } from "@/api/artifacts";
import { AGENT_DISPLAY_NAMES } from "@/lib/agent-ids";
import type { Artifact } from "@/types/api";
import { cn } from "@/lib/utils";
import { tp } from "@/lib/plural";

/** Arquivos que a API monta do banco no download (CSV de mapeamento, ZIP de dados): o
 * formato é dela, então a linha tem um botão só, e montar pode levar tempo. */
const SERVER_BUILT_TYPES = new Set(["erp_config_extraction", "erp_config_extraction_data"]);

type Kind = "xlsx" | "json" | "pdf" | "xml" | "md" | "csv";

const KIND_META: Record<Kind, { bg: string; fg: string; Icon: LucideIcon }> = {
  xlsx: { bg: "rgba(39,161,85,0.1)", fg: "#27a155", Icon: FileSpreadsheet },
  json: { bg: "rgba(255,200,61,0.15)", fg: "#b57a00", Icon: FileCode2 },
  pdf: { bg: "rgba(212,53,60,0.1)", fg: "#b02a31", Icon: FileText },
  xml: { bg: "rgba(29,78,160,0.1)", fg: "#1d4ea0", Icon: FileCode },
  md: { bg: "rgba(123,135,153,0.1)", fg: "#596b85", Icon: FileText },
  csv: { bg: "rgba(8,37,76,0.08)", fg: "#08254c", Icon: FileSpreadsheet },
};

const EXTENSION_TO_KIND: Record<string, Kind> = {
  xlsx: "xlsx",
  xls: "xlsx",
  pdf: "pdf",
  xml: "xml",
  md: "md",
  csv: "csv",
};

function kindForArtifact(artifact: Artifact): Kind {
  const ext = artifact.file_path?.split(".").pop()?.toLowerCase();
  return (ext && EXTENSION_TO_KIND[ext]) || "json";
}

/** Espelha `_is_table`/`tabulate_content` do backend (services/api/app/services
 * /artifact_tabulate.py): só é "planilhável" quem tem, no topo, uma lista de
 * registros. Um relatório embrulhado como `{content: "<markdown>"}` (ou
 * qualquer dict só de escalares) não tem — pra esses, XLSX vira MD. */
function hasTabularData(content: unknown): boolean {
  const isRecordList = (value: unknown): boolean =>
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => item !== null && typeof item === "object" && !Array.isArray(item));

  if (isRecordList(content)) return true;
  if (content && typeof content === "object" && !Array.isArray(content)) {
    return Object.values(content as Record<string, unknown>).some(isRecordList);
  }
  return false;
}

/** `{value, unit}` para exibição — só formata, nunca decide se o total é conhecido. */
function formatBytes(bytes: number): { value: string; unit: string } {
  if (bytes < 1024) return { value: bytes.toString(), unit: "B" };
  const units = ["KB", "MB", "GB"];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return { value: n.toFixed(1), unit: units[i] };
}

export default function ProjectArtifacts() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project } = useProject(projectId);
  const {
    data: artifacts,
    isLoading: isLoadingArtifacts,
    isError: isArtifactsError,
    refetch: refetchArtifacts,
  } = useArtifacts(projectId);
  // Chaves `${artifactId}:${format}` dos downloads em andamento. É um conjunto, e
  // não uma chave só, porque os downloads se sobrepõem: o ZIP de dados leva minutos
  // sendo montado no servidor, e o consultor pode baixar outro artefato nesse meio
  // tempo. Com uma chave única, o fim do segundo zerava o estado do primeiro e o
  // botão do ZIP voltava a habilitar com o servidor ainda trabalhando.
  const [downloadingKeys, setDownloadingKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [query, setQuery] = useState("");

  // KPIs do header refletem o projeto inteiro — a busca é só um filtro de
  // visualização da tabela, não muda quantos artefatos o projeto tem.
  const count = artifacts?.length ?? 0;
  // `size_bytes` null pode ser transiente (storage lento) ou permanente
  // (objeto sumiu — `stat_size` nunca resolve). Skeleton é só para o
  // primeiro caso: enquanto a query ainda carrega. Depois disso, um `null`
  // vira "conhecido parcialmente" — soma o que dá e sinaliza que é parcial,
  // em vez de um skeleton que nunca sai.
  const hasUnknownSize = artifacts?.some((a) => a.size_bytes == null) ?? false;
  const totalBytes = artifacts?.reduce((sum, a) => sum + (a.size_bytes ?? 0), 0) ?? 0;

  const trimmedQuery = query.trim().toLowerCase();
  const filteredArtifacts = trimmedQuery
    ? (artifacts ?? []).filter((a) => a.title.toLowerCase().includes(trimmedQuery))
    : (artifacts ?? []);
  const groups = groupByAgentAndExecution(filteredArtifacts);
  const isSearching = trimmedQuery.length > 0;

  async function handleDownload(artifact: Artifact, format: DownloadFormat) {
    if (!projectId) return;
    const key = `${artifact.id}:${format}`;
    setDownloadingKeys((prev) => new Set(prev).add(key));
    try {
      await downloadArtifact(projectId, artifact.id, format);
      toast.success(t("Artifact downloaded"));
    } catch (err) {
      console.error("download do artifact falhou", err);
      toast.error(t("Failed to download artifact"));
    } finally {
      // Remove só a própria chave: outros downloads continuam ocupando suas linhas.
      setDownloadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  const sizeDisplay = formatBytes(totalBytes);

  if (isArtifactsError) {
    return (
      <ErrorState message={t("Failed to load artifacts")} onRetry={() => refetchArtifacts()} />
    );
  }

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<FileText />}
        overline={`${(project?.name || "PROJECT").toUpperCase()} · ${t("DELIVERABLES")}`}
        title={t("Artifacts")}
        meta={<HeroMetaItem k={t("Project")} v={project?.name || "—"} />}
        kpis={[
          {
            label: t("Artifacts"),
            value: isLoadingArtifacts ? (
              <Skeleton className="h-8 w-10 inline-block bg-white/10" />
            ) : (
              <span data-testid="artifacts-count">{count}</span>
            ),
            sub: t("this project"),
          },
          {
            label: t("Storage Used"),
            value: isLoadingArtifacts ? (
              <Skeleton
                data-testid="artifacts-size-skeleton"
                className="h-8 w-20 inline-block bg-white/10"
              />
            ) : (
              <span data-testid="artifacts-total-size">
                {hasUnknownSize && "≥ "}
                {sizeDisplay.value}
                <span className="text-sm text-white/40 font-semibold tracking-normal ml-1">
                  {sizeDisplay.unit}
                </span>
              </span>
            ),
            sub: !isLoadingArtifacts && hasUnknownSize ? t("Some sizes unavailable") : undefined,
          },
        ]}
      />

      <div className="p-8 space-y-5">
        <Card>
          <Toolbar>
            <div className="h-8 bg-[#f7f7f7] border border-[#e8ebf0] rounded-md flex items-center px-2.5 gap-2 text-xs text-[#7b8799] flex-[0_1_280px]">
              <Search className="w-3 h-3 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="border-0 outline-0 bg-transparent flex-1 text-xs text-[#08254c] placeholder:text-[#7b8799] min-w-0"
                placeholder={t("Search artifacts in this project…")}
              />
            </div>
          </Toolbar>

          {isLoadingArtifacts ? (
            <div data-testid="artifacts-table-loading" className="p-4 space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[#7b8799]">
              {isSearching ? t("No artifacts match your search") : t("No artifacts yet")}
            </div>
          ) : (
            groups.map(({ agentId, executionId, artifacts: groupArtifacts }, i) => (
              <AgentGroup
                key={`${agentId}:${executionId}`}
                agentId={agentId}
                executionId={executionId}
                artifacts={groupArtifacts}
                showHeader={i === 0}
                border={i > 0}
                downloadingKeys={downloadingKeys}
                onDownload={handleDownload}
              />
            ))
          )}
        </Card>

        <Mantra>
          {t("ONE PROJECT")} · <MantraAccent>{count} {t("Artifacts")}</MantraAccent> · {t("FULL LINEAGE")}
        </Mantra>
      </div>
    </div>
  );
}

interface ArtifactGroup {
  agentId: string;
  executionId: string | null;
  artifacts: Artifact[];
}

/** Uma seção por (agente, execução) — dois runs do mesmo agente não se
 * misturam, mesmo quando um dá sequência ao outro. `execution_id` é nullable
 * (`ON DELETE SET NULL`): artefatos órfãos do mesmo agente caem juntos num
 * grupo "sem execução" em vez de um por artefato. */
function groupByAgentAndExecution(artifacts: Artifact[]): ArtifactGroup[] {
  const order: string[] = [];
  const map = new Map<string, ArtifactGroup>();
  for (const a of artifacts) {
    const key = `${a.agent_id}::${a.execution_id ?? "none"}`;
    if (!map.has(key)) {
      map.set(key, { agentId: a.agent_id, executionId: a.execution_id, artifacts: [] });
      order.push(key);
    }
    map.get(key)!.artifacts.push(a);
  }
  return order.map((key) => map.get(key)!);
}

function AgentGroup({
  agentId,
  executionId,
  artifacts,
  border,
  showHeader,
  downloadingKeys,
  onDownload,
}: {
  agentId: string;
  executionId: string | null;
  artifacts: Artifact[];
  border?: boolean;
  showHeader?: boolean;
  downloadingKeys: ReadonlySet<string>;
  onDownload: (artifact: Artifact, format: DownloadFormat) => void;
}) {
  const { t, i18n } = useTranslation();
  const numberFormat = new Intl.NumberFormat(i18n.language || "en-US");
  const label = AGENT_DISPLAY_NAMES[agentId] ?? agentId;

  function sizeLabel(a: Artifact): string {
    if (a.item_count != null && a.item_unit) {
      // O número entra já formatado no idioma da tela; a forma (singular/plural)
      // vem do valor numérico, porque "1.194.216" não diz nada ao `tp`.
      const n = numberFormat.format(a.item_count);
      return a.item_unit === "fields"
        ? tp(t, a.item_count, "{{n}} field", "{{n}} fields", { n })
        : tp(t, a.item_count, "{{n}} record", "{{n}} records", { n });
    }
    if (a.size_bytes == null) return "—";
    const { value, unit } = formatBytes(a.size_bytes);
    return `${value} ${unit}`;
  }
  return (
    <>
      <div className={cn("px-4 pt-3.5 pb-1", border && "border-t border-[#f2f4f7]")}>
        <div className="font-mono text-[11px] text-[#596b85] tracking-[0.14em] uppercase font-semibold mb-2 flex items-center gap-2.5">
          {label}
          <span className="mono normal-case tracking-normal text-[#7b8799]">
            {executionId ? executionId.slice(0, 8) : t("No execution")}
          </span>
          <span className="bg-[#f2f4f7] text-[#7b8799] px-2 py-0.5 rounded-full text-[9px]">
            {artifacts.length} {t("files")}
          </span>
        </div>
      </div>
      <table className="tbl">
        <thead className={showHeader ? undefined : "sr-only"}>
          <tr>
            <th style={{ width: "40%" }}>{t("File")}</th>
            <th>{t("Size")}</th>
            <th>{t("Created")}</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {artifacts.map((a) => {
            const kind = kindForArtifact(a);
            const m = KIND_META[kind];
            const rowBusy = [...downloadingKeys].some((key) => key.startsWith(`${a.id}:`));
            return (
              <tr key={a.id} className="hover:bg-[#f7f9fc]">
                <td style={{ width: "40%" }}>
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                      style={{ background: m.bg, color: m.fg }}
                    >
                      <m.Icon className="w-[15px] h-[15px]" strokeWidth={1.75} />
                    </div>
                    <div>
                      <div className="name">{a.title}</div>
                      <div className="mono muted">{a.artifact_type}</div>
                    </div>
                  </div>
                </td>
                <td className="mono">{sizeLabel(a)}</td>
                <td className="mono">{new Date(a.created_at).toLocaleDateString()}</td>
                <td style={{ width: 40 }} className="text-right whitespace-nowrap">
                  {SERVER_BUILT_TYPES.has(a.artifact_type) ? (
                    <DownloadButton
                      label={t("Download")}
                      artifactTitle={a.title}
                      busy={rowBusy}
                      busyLabel={t("Preparing file…")}
                      disabled={rowBusy}
                      onClick={() => onDownload(a, "json")}
                    />
                  ) : a.file_path ? (
                    <DownloadButton
                      label={t("Download")}
                      artifactTitle={a.title}
                      busy={rowBusy}
                      disabled={rowBusy}
                      onClick={() => onDownload(a, "json")}
                    />
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        aria-label={`${t("Download")} — ${a.title}`}
                        aria-busy={rowBusy}
                        disabled={rowBusy}
                        className={DOWNLOAD_TRIGGER_CLASS}
                      >
                        {rowBusy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onDownload(a, "csv")}>
                          {t("Download CSV")}
                        </DropdownMenuItem>
                        {hasTabularData(a.content) ? (
                          <DropdownMenuItem onClick={() => onDownload(a, "xlsx")}>
                            {t("Download XLSX")}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => onDownload(a, "md")}>
                            {t("Download MD")}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/** Ícone único de download — mesmo estilo usado pelo `DropdownMenuTrigger`
 * (arquivo real: um clique já resolve, não há formato pra escolher). */
const DOWNLOAD_TRIGGER_CLASS = cn(
  "inline-flex items-center justify-center w-7 h-7 rounded-md border border-transparent",
  "bg-transparent text-[#7b8799] hover:border-[#e0e6ed] hover:bg-[#f7f7f7] hover:text-[#08254c]",
  "disabled:opacity-50 disabled:pointer-events-none",
);

function DownloadButton({
  label,
  artifactTitle,
  busy,
  busyLabel,
  disabled,
  onClick,
}: {
  label: string;
  artifactTitle: string;
  busy: boolean;
  busyLabel?: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const showBusyLabel = busy && Boolean(busyLabel);
  return (
    <button
      type="button"
      aria-label={`${label} — ${artifactTitle}`}
      aria-busy={busy}
      disabled={disabled}
      onClick={onClick}
      className={cn(DOWNLOAD_TRIGGER_CLASS, showBusyLabel && "w-auto px-2 gap-1.5")}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      {showBusyLabel ? <span className="text-[11px]">{busyLabel}</span> : null}
    </button>
  );
}
